import React, { useMemo, lazy, Suspense } from "react";
import { Image, Text, TouchableOpacity, View } from "react-native";
import { AppFontStyle } from "../../styles/AppFontStyle";
import { AppColors } from "../../utility/AppColors";
import { AppImages } from "../../utility/AppImages";
import { DEVICE_SIZES, useDeviceSize } from "rn-responsive-styles";
import { useLiveStream } from "../../context/LiveStreamContext";
import { streamingToolsUrl } from "../../Api/configs";
import StreamActionIcon from "../StreamActionIcons";
import { useWsProvider } from "../../context/NativeApplicationContext";

// Lazy load the manage sources modal
const ManageSourcesModal = lazy(() => import("./ManageSourcesModal"));

interface StreamerViewProps {
  setIsShare: (value: boolean) => void;
  isManageSourcesPopupVisible: boolean;
  onManageSourcesPress: () => void;
}

// Memoized viewer component
const ViewersList = React.memo(
  ({ viewerDetails }: { viewerDetails: any[] }) => {
    const uniqueViewers = useMemo(() => {
      if (!viewerDetails || viewerDetails.length === 0) return [];

      // Deduplicate and limit to 10
      const viewerMap = new Map(
        viewerDetails.map((details) => [details.userId, details])
      );
      return Array.from(viewerMap.values()).slice(0, 10);
    }, [viewerDetails]);

    if (uniqueViewers.length === 0) {
      return (
        <Text style={{ color: "gray", fontSize: 14 }}>No live Viewers</Text>
      );
    }

    return (
      <>
        {uniqueViewers.map((details: any, index: number) => (
          <Image
            key={details.userId}
            source={{
              uri: details.profilePicture ?? AppImages.Ellipse1,
            }}
            style={{
              height: 19,
              width: 19,
              borderRadius: 100,
              right: index * 12,
            }}
            resizeMode="contain"
          />
        ))}
      </>
    );
  }
);

ViewersList.displayName = "ViewersList";

const StreamerView: React.FC<StreamerViewProps> = ({
  setIsShare,
  isManageSourcesPopupVisible,
  onManageSourcesPress,
}) => {
  const deviceSize = useDeviceSize();
  const { totalGiftedTokens, totalViewers, organizerData, viewerDetails } =
    useLiveStream();
  const { isNativeAppConnected, broadcasterId } = useWsProvider();

  // Build streaming tools URL with broadcaster ID
  const getStreamingUrl = useMemo(() => {
    if (!broadcasterId) return null;
    return `${streamingToolsUrl}/preview?broadcasterId=${encodeURIComponent(broadcasterId)}`;
  }, [broadcasterId]);

  const isMobile = useMemo(
    () => deviceSize === DEVICE_SIZES.XS || deviceSize === DEVICE_SIZES.SM,
    [deviceSize]
  );

  const containerStyle = useMemo(
    () => ({
      width: isMobile ? "100%" : ("46.5%" as any),
      gap: 12,
    }),
    [isMobile]
  );

  const previewContainerStyle = useMemo(
    () =>
      !isMobile
        ? {
            borderColor: AppColors.red_AF0707,
            borderWidth: 1,
            borderStyle: "dashed" as const,
            paddingVertical: 10,
            paddingHorizontal: 20,
          }
        : {},
    [isMobile]
  );

  const statsContainerStyle = useMemo(
    () => ({
      paddingHorizontal: isMobile ? 9 : 12,
      paddingVertical: isMobile ? 9 : 12,
      backgroundColor: AppColors.grayColor_282828,
      borderRadius: 12,
      flexDirection: "row" as const,
      justifyContent: "space-between" as const,
      alignItems: "center" as const,
    }),
    [isMobile]
  );

  return (
    <View style={containerStyle}>
      {/* Manage Sources Modal - Lazy Loaded */}
      {isManageSourcesPopupVisible && (
        <Suspense fallback={null}>
          <ManageSourcesModal
            visible={isManageSourcesPopupVisible}
            onClose={onManageSourcesPress}
          />
        </Suspense>
      )}

      {/* Preview Container */}
      <View style={previewContainerStyle}>
        {isNativeAppConnected && getStreamingUrl ? (
          <iframe
            key={broadcasterId}
            src={getStreamingUrl}
            style={{
              height: isMobile ? 223 : 393,
              width: "100%",
              border: "none",
            }}
          />
        ) : (
          <View
            style={{
              height: isMobile ? 223 : 393,
              width: "100%",
              backgroundColor: AppColors.grayColor_282828,
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Text style={{ color: AppColors.gray_7E7E7E, fontSize: 14 }}>
              {isNativeAppConnected ? "Loading preview..." : "Native app not connected"}
            </Text>
          </View>
        )}
      </View>

      {/* Stats Container */}
      <View style={statsContainerStyle}>
        {/* Manage Sources Button */}
        <TouchableOpacity
          onPress={onManageSourcesPress}
          style={{
            backgroundColor: isManageSourcesPopupVisible
              ? AppColors.gray_7E7E7E
              : AppColors.appViolentColor_6D08C3,
            paddingHorizontal: 12,
            paddingVertical: 9,
            borderRadius: 6,
            flexDirection: "row",
            alignItems: "center",
            width: 170,
          }}
        >
          <Image
            source={
              isManageSourcesPopupVisible
                ? AppImages.Souces_Close
                : AppImages.Sources_Edit
            }
            style={{ width: 20, height: 20, marginRight: 8 }}
          />
          <Text style={{ color: AppColors.white, fontSize: 14 }}>
            {isManageSourcesPopupVisible ? "Close sources" : "Manage Sources"}
          </Text>
        </TouchableOpacity>

        {/* Likes */}
        <View style={{ gap: 3 }}>
          <Text
            style={[
              isMobile
                ? AppFontStyle.REGULAR_ANYBODY_10
                : AppFontStyle.REGULAR_ANYBODY_12,
              { color: AppColors.gray_C3C3C3 },
            ]}
          >
            Likes
          </Text>
          <Text
            style={
              isMobile
                ? AppFontStyle.REGULAR_ANYBODY_12
                : AppFontStyle.REGULAR_ANYBODY_16
            }
          >
            {organizerData?.likes ?? 0}
          </Text>
        </View>

        {/* Viewers */}
        <View style={{ gap: 3 }}>
          <Text
            style={[
              isMobile
                ? AppFontStyle.REGULAR_ANYBODY_10
                : AppFontStyle.REGULAR_ANYBODY_12,
              { color: AppColors.gray_C3C3C3 },
            ]}
          >
            Total Viewers: {totalViewers ?? 0}
          </Text>
          <View
            style={{ marginTop: 10, alignSelf: "center", flexDirection: "row" }}
          >
            <ViewersList viewerDetails={viewerDetails} />
          </View>
        </View>

        {/* Tokens Gifted */}
        <View style={{ flexDirection: "row", gap: 6, alignItems: "center" }}>
          <Image
            source={AppImages.icGift}
            style={{ height: 32, width: 32 }}
            resizeMode="contain"
          />
          <Text
            style={{
              color: AppColors.white,
              fontSize: isMobile ? 14 : 16,
              fontWeight: "600",
            }}
          >
            {totalGiftedTokens ?? 0} gifts
          </Text>
        </View>

        {/* Share Button */}
        <StreamActionIcon type="share" onPress={() => setIsShare(true)} />
      </View>
    </View>
  );
};

export default StreamerView;
